import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AppBar from '../components/AppBar'
import LoadingSpinner from '../components/LoadingSpinner'
import SubmitButton from '../components/SubmitButton'
import {
  getTasteProfile,
  TasteProfile as TasteProfileData,
} from '../api/tasteProfile'

const PLACEHOLDER = '/images/profile-placeholder-icon.png'

/** 막대 색. 알파를 쓰면 겹칠 때 brandPrimary보다 진해져서 불투명색으로 둔다 */
const BAR_FILL = '#6E5C4B'
const BAR_TRACK = '#EDE7DC'

/** 사진 지름과 두 사진이 물리는 폭 */
const DISC = 80
const OVERLAP = 20

/**
 * 나 ◐ 상대.
 *
 * 겹쳐 놓기만 하면 두 사진의 경계가 뭉개진다. 위에 오는 쪽에 배경색 테두리를
 * 둘러 경계를 만든다 — 테두리 색이 화면 바탕과 같아서 선이 아니라 '사이'로 읽힌다.
 */
const ProfilePair: React.FC<{
  me: string | null
  target: string | null
  targetName: string
}> = ({ me, target, targetName }) => {
  const ring = 3
  const width = DISC * 2 - OVERLAP + ring

  const photo = (src: string | null, alt: string) => (
    <img
      src={src || PLACEHOLDER}
      alt={alt}
      draggable={false}
      onError={(e) => {
        e.currentTarget.src = PLACEHOLDER
      }}
      className="w-full h-full rounded-full object-cover bg-E8E1D9"
      style={{ opacity: src ? 1 : 0.8 }}
    />
  )

  return (
    <div className="relative mx-auto" style={{ width, height: DISC + ring * 2 }}>
      <div
        className="absolute rounded-full overflow-hidden"
        style={{ left: 0, top: ring, width: DISC, height: DISC }}
      >
        {photo(me, '내 프로필')}
      </div>
      <div
        className="absolute rounded-full overflow-hidden"
        style={{
          left: DISC - OVERLAP,
          top: 0,
          width: DISC + ring * 2,
          height: DISC + ring * 2,
          padding: ring,
          background: '#FAF6EF',
        }}
      >
        {photo(target, targetName)}
      </div>
    </div>
  )
}

/** 한 사람의 진척 — "먹짱님 · 기록 7개 … 3개 필요" */
const ProgressRow: React.FC<{ name: string; total: number; min: number }> = ({
  name,
  total,
  min,
}) => {
  const need = Math.max(0, min - total)
  const done = need === 0

  return (
    <div className="mt-18px first:mt-0">
      <div className="flex items-baseline justify-between gap-10px">
        <span className="text-15px leading-150% text-textColorText2 truncate">
          <b className="font-bold text-brown-80%-main">{name}</b>님 · 기록{' '}
          {total}개
        </span>
        <span
          className="shrink-0 font-pretendard font-bold text-14px"
          style={{ color: done ? BAR_FILL : '#A08A6B' }}
        >
          {done ? '완료' : `${need}개 필요`}
        </span>
      </div>

      <div
        className="mt-8px h-8px rounded-full overflow-hidden"
        style={{ background: BAR_TRACK }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(100, (total / min) * 100)}%`,
            background: BAR_FILL,
          }}
        />
      </div>
    </div>
  )
}

const TasteProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<TasteProfileData | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    let alive = true
    getTasteProfile(userId)
      .then((res) => alive && setData(res))
      .catch((e) => {
        if (!alive) return
        setError(e?.response?.data?.message ?? '분석을 불러오지 못했어요.')
      })
    return () => {
      alive = false
    }
  }, [userId])

  /*
   * 카테고리 분석에 올릴 세 줄.
   *
   * 막대는 개수가 아니라 자기 기록 안에서의 비중(%)이다. 기록 수가 다른 두
   * 사람을 개수로 견주면 많이 쓴 쪽이 모든 축에서 커 보인다.
   *
   * 고르는 기준은 **기록이 많은 순**이다 — 두 사람의 기록 수를 합쳐 센다.
   * 비중으로 고르면 기록이 적은 사람의 한 건이 100%가 되어 맨 위로 올라온다.
   * 여섯 축을 다 내리면 0%짜리 빈 줄이 섞여 읽을 게 흐려지므로 셋에서 끊는다.
   */
  const TOP_N = 3
  const rows = useMemo(() => {
    if (!data) return []
    const share = (side: { total: number; counts: Record<string, number> }, k: string) =>
      side.total > 0 ? ((side.counts[k] ?? 0) / side.total) * 100 : 0

    return data.axes
      .map((axis) => ({
        axis,
        mine: share(data.me, axis),
        theirs: share(data.target, axis),
        records: (data.me.counts[axis] ?? 0) + (data.target.counts[axis] ?? 0),
      }))
      .filter((r) => r.records > 0)
      .sort((a, b) => b.records - a.records)
      .slice(0, TOP_N)
  }, [data])

  /** 막대 길이는 가장 큰 비중을 꽉 찬 길이로 둔다. 비어 보이지 않게 */
  const scale = useMemo(
    () => Math.max(...rows.map((r) => Math.max(r.mine, r.theirs)), 1),
    [rows]
  )

  if (!data && !error) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <AppBar title="취향 유사도" isBottomBorder onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size={28} borderWidth={2} />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
        <AppBar title="취향 유사도" isBottomBorder onBack={() => navigate(-1)} />
        <div className="flex-1 flex items-center justify-center px-20px text-center text-14px text-textColorText2">
          {error}
        </div>
      </div>
    )
  }

  /*
   * 서버 값이 먼저다. 다만 웹 번들과 백엔드가 동시에 바뀌지는 않는다 —
   * 웹뷰가 새 번들을 캐시에서 먼저 집으면 옛 응답(이 필드가 없는)을 받는 순간이
   * 있다. 그때 NaN이 퍼져 화면이 통째로 깨지는 것보다, 기본값으로 버티는 편이 낫다.
   */
  const min = data.min_records ?? 10
  const myName = data.me.nickname || '나'
  const myNeed = Math.max(0, min - data.me.total)
  const theirNeed = Math.max(0, min - data.target.total)
  const ready = myNeed === 0 && theirNeed === 0

  /*
   * 모자랄 때의 머리말 — 누구를 기다리는지만 말한다.
   *
   * 몇 개가 모자란지는 바로 아래 줄에 사람별로 다 적혀 있다("3개 필요").
   * 머리말에까지 숫자를 넣으면 같은 수를 두 번 읽게 되고, 양쪽이 다 모자랄
   * 때는 어느 쪽 숫자인지도 흐려진다.
   */
  const waitingName =
    myNeed > 0 && theirNeed > 0
      ? '두 분'
      : myNeed > 0
        ? `${myName}님`
        : `${data.target.nickname}님`
  const waitingHeadline = `${waitingName}의 기록이 더 쌓이면 결과를 볼 수 있어요`

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar title="취향 유사도" isBottomBorder onBack={() => navigate(-1)} />

      <div className="app-container flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-20px pt-30px pb-20px">
          <ProfilePair
            me={data.me.profile_image}
            target={data.target.profile_image}
            targetName={data.target.nickname}
          />

          <p className="mt-16px text-center text-16px leading-150% text-textColorText2">
            <b className="font-bold text-brown-80%-main">{myName}</b>
            {' & '}
            <b className="font-bold text-brown-80%-main">
              {data.target.nickname}
            </b>
            님의 취향 유사도
          </p>

          <p className="mt-10px text-center font-pretendard font-bold text-30px leading-tight text-brown-80%-main">
            {ready ? `${data.similarity}%` : '분석 중'}
          </p>

          {!ready && (
            <div
              className="mt-24px rounded-16px bg-white px-20px py-20px"
              style={{ boxShadow: '0 1px 3px rgba(75,54,33,.05)' }}
            >
              <p className="font-pretendard font-bold text-16px leading-150% text-brown-80%-main">
                {waitingHeadline}
              </p>

              <div className="mt-16px">
                <ProgressRow
                  name={myName}
                  total={data.me.total}
                  min={min}
                />
                <ProgressRow
                  name={data.target.nickname}
                  total={data.target.total}
                  min={min}
                />
              </div>
            </div>
          )}

          {ready && (
            <>
              {/* 카테고리 분석 — 가운데 이름을 두고 좌우로 뻗는 막대 */}
              <section className="mt-16px">
                <h2 className="font-pretendard font-bold text-16px text-brown-80%-main">
                  카테고리 분석
                </h2>

                {/*
                  이름은 제목("카테고리 분석")과 같은 선에서 시작한다.
                  아래 % 숫자는 줄 배경(px-10px) 안쪽이라 조금 들어가 있는데,
                  이름까지 같이 들여쓰면 제목과 어긋나 보인다. 바깥 선에 맞춘다.
                */}
                <div className="mt-14px flex items-center justify-between gap-12px text-13px text-textColorText3">
                  <span className="min-w-0 truncate">{myName}님</span>
                  <span className="min-w-0 truncate text-right">
                    {data.target.nickname}님
                  </span>
                </div>

                {/* 기록은 충분한데 분류를 아는 장소가 하나도 없을 수 있다 */}
                {rows.length === 0 && (
                  <p className="mt-12px text-14px leading-160% text-textColorText3">
                    아직 종류를 알 수 있는 기록이 없어요
                  </p>
                )}

                <div className="mt-6px">
                  {rows.map((r, i) => (
                    <div
                      key={r.axis}
                      className={`flex items-center gap-8px px-10px py-10px rounded-12px ${
                        i === 0 ? 'bg-472410-5%' : ''
                      }`}
                    >
                      <span className="w-38px shrink-0 text-left font-pretendard font-bold text-14px tabular-nums text-brown-80%-main">
                        {Math.round(r.mine)}%
                      </span>
                      <div className="flex-1 flex justify-end">
                        <div
                          className="h-10px rounded-full"
                          style={{
                            width: `${Math.max(6, (r.mine / scale) * 100)}%`,
                            background: BAR_FILL,
                          }}
                        />
                      </div>
                      <span className="w-44px shrink-0 text-center font-pretendard font-bold text-14px text-brown-80%-main">
                        {r.axis}
                      </span>
                      <div className="flex-1">
                        <div
                          className="h-10px rounded-full"
                          style={{
                            width: `${Math.max(6, (r.theirs / scale) * 100)}%`,
                            background: BAR_FILL,
                          }}
                        />
                      </div>
                      <span className="w-38px shrink-0 text-right font-pretendard font-bold text-14px tabular-nums text-brown-80%-main">
                        {Math.round(r.theirs)}%
                      </span>
                    </div>
                  ))}
                </div>
              </section>

              {/* 근거 세 문장. 번호가 문장의 앵커가 된다 */}
              <section className="mt-16px">
                <h2 className="font-pretendard font-bold text-16px text-brown-80%-main">
                  {data.target.nickname}님의 취향 요약
                </h2>

                <ol className="mt-12px">
                  {data.lines.map((line, i) => (
                    <li key={i} className="flex items-center gap-14px py-10px">
                      {/* 번호는 랭킹 화면과 같은 덩치라 문장의 앵커가 된다 */}
                      <span className="w-16px shrink-0 text-center font-pretendard font-bold text-24px leading-none text-brown-80%-main tabular-nums">
                        {i + 1}
                      </span>
                      <span className="flex-1 text-16px leading-160% text-textColorText2">
                        {line}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            </>
          )}
        </div>
      </div>

      {/* 모자란데 내 몫이 아니면 버튼을 내보내지 않는다 — 누를 이유가 없다 */}
      {(ready || myNeed > 0) && (
        <div className="app-container px-20px pt-12px pb-24px">
          <SubmitButton
            text={ready ? '내 취향 더 알아보기' : `${myNeed}곳 기록하기`}
            onSubmit={() => navigate(ready ? '/main-home' : '/search')}
          />
        </div>
      )}
    </div>
  )
}

export default TasteProfile
